import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { serverMessageSchema, type EntityChannel, type ServerMessage } from '@monrdv/shared';
import { deferUpdate, isDirty } from './liveEditing.js';

/**
 * Live updates.
 *
 * Requirement: the interface should update as other people work, without anyone pressing
 * refresh, and without breaking.
 *
 * The socket only ever carries *"this changed"* — never the changed rows. Receiving an
 * invalidation marks the affected queries stale and TanStack Query refetches them through
 * the ordinary REST endpoint. That is what keeps one code path producing data, so a live
 * update can never disagree with what a page load would have shown.
 *
 * A dropped socket therefore costs freshness, not correctness. It reconnects with backoff,
 * and while it is down the app is merely as stale as any app without live updates.
 */

export type ConnectionState = 'connecting' | 'open' | 'reconnecting' | 'offline';

/**
 * Which query keys an entity change should invalidate.
 *
 * Partial on purpose. `ENTITY_CHANNELS` is the whole system's vocabulary, and a good part of
 * it is the field app's — an asset, a hazard, a meter reading and a visit request have no
 * screen in the portal, so there is nothing here to refetch when one changes. The lookup
 * below falls back to an empty list, which is the same "ignore it" the exhaustive form would
 * have had to spell out as a dozen empty arrays that then go stale as channels are added.
 */
const AFFECTED: Partial<Record<EntityChannel, string[][]>> = {
  customer: [['customers'], ['search']],
  job: [['jobs'], ['board'], ['today'], ['search']],
  appointment: [['appointments'], ['jobs'], ['board'], ['today'], ['search']],
  quote: [['quotes'], ['jobs'], ['search']],
  invoice: [['invoices'], ['jobs'], ['receivables'], ['search']],
  payment: [['payments'], ['invoices'], ['receivables']],
  thread: [['threads'], ['inbox']],
  message: [['threads'], ['inbox'], ['messages']],
  time_entry: [['timeEntries'], ['jobs']],
  call: [['calls'], ['search']],
  route: [['routes'], ['board']],
  calendar_event: [['calendar'], ['board']],
  /*
   * A person's colour and name are drawn on the board, on every visit card and in the search
   * results, so a team change touches more screens than it looks like it should.
   */
  team: [['team'], ['board'], ['today'], ['search']],
};

export function useRealtime(currentUserId: string | null): ConnectionState {
  const queryClient = useQueryClient();
  const [state, setState] = useState<ConnectionState>('connecting');
  const socketRef = useRef<WebSocket | null>(null);
  const attemptRef = useRef(0);
  const timerRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    /*
     * No identity yet, so nothing to listen as.
     *
     * `/ws` authenticates with the same session cookie as the REST API and closes with 4401
     * when there is none. Dialling before the session query resolves — or on the sign-in
     * screen, where there will never be a session at all — therefore buys an immediate close
     * followed by a backoff loop retrying something that cannot succeed, and a console full
     * of connection errors on the one page where they are expected.
     *
     * The guard belongs here rather than at the call site: `AppShell` invokes this hook above
     * its own early return for `/connexion`, deliberately, so that hook order cannot change
     * between renders.
     */
    if (!currentUserId) {
      setState('connecting');
      return;
    }

    let disposed = false;

    const connect = () => {
      if (disposed) return;

      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const socket = new WebSocket(`${protocol}//${window.location.host}/ws`);
      socketRef.current = socket;

      socket.onopen = () => {
        attemptRef.current = 0;
        setState('open');
        // Anything that changed while the socket was down was missed, so refetch
        // everything active on reconnect. Without this a coordinator who lost signal for a
        // minute keeps looking at a stale board and does not know it.
        void queryClient.invalidateQueries();
      };

      socket.onmessage = (event) => {
        let parsed;
        try {
          parsed = serverMessageSchema.safeParse(JSON.parse(event.data));
        } catch {
          return;
        }
        if (!parsed.success) return;
        handle(parsed.data);
      };

      socket.onclose = () => {
        if (disposed) return;
        setState(navigator.onLine ? 'reconnecting' : 'offline');
        // Exponential backoff with a ceiling, plus jitter so twenty tabs coming back from a
        // network blip do not reconnect in lockstep.
        const delay = Math.min(1000 * 2 ** attemptRef.current, 30_000);
        attemptRef.current += 1;
        timerRef.current = window.setTimeout(connect, delay + Math.random() * 500);
      };

      socket.onerror = () => socket.close();
    };

    const handle = (message: ServerMessage) => {
      if (message.type === 'notification') {
        // Refetch rather than splicing the event into the cache: the tray also needs the
        // recomputed action and unread counts, and the server owns the grouping that
        // collapses twelve messages in one thread into a single row.
        void queryClient.invalidateQueries({ queryKey: ['notifications'] });
        return;
      }

      if (message.type === 'invalidate') {
        // Skip the echo of our own change: the optimistic update is already correct, and
        // refetching would make the row flicker back and forth.
        if (message.originUserId && message.originUserId === currentUserId) return;

        const targets = [{ entity: message.entity, id: message.id }, ...message.also];
        for (const target of targets) {
          // Never refetch a record somebody is currently typing into — that would erase
          // their half-finished edit with no explanation. Hold the update and let the
          // editor offer it instead. Lists and everything else still refresh normally.
          if (target.id && isDirty(target.entity, target.id)) {
            deferUpdate(target.entity, target.id);
            continue;
          }
          for (const key of AFFECTED[target.entity] ?? []) {
            void queryClient.invalidateQueries({ queryKey: key });
          }
          if (target.id) {
            void queryClient.invalidateQueries({ queryKey: [target.entity, target.id] });
          }
        }
      }
    };

    connect();

    // A tab that has been in the background is almost certainly stale, and the socket may
    // have been suspended without a close event.
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (socketRef.current?.readyState !== WebSocket.OPEN) {
        socketRef.current?.close();
      } else {
        void queryClient.invalidateQueries();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onVisible);

    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onVisible);
      if (timerRef.current) window.clearTimeout(timerRef.current);
      socketRef.current?.close();
    };
  }, [queryClient, currentUserId]);

  return state;
}
